/**
 * The client half of the cloud-identity claim — `notes/identity/PLAN.md` F13.
 *
 * **Why this exists.** The WebRTC tunnel only mints a local hub JWT for a cloud
 * user the hub already maps in `users.cloud_user_id`. On a hub where nobody is
 * linked yet, every hub call answers `401 identity_not_linked`, and
 * `POST /users/:id/link-cloud` cannot break that deadlock because it needs the
 * very local token the tunnel refuses to mint. `POST /auth/claim` can: it is the
 * one tunneled path an unmapped session may call, it validates the client's
 * cloud token against the cloud's `/api/oauth2/userinfo`, matches the hub user
 * by the *verified* cloud email, links it, and answers `{user, token}`.
 *
 * **Why the decision lives here, pure.** The expensive mistake this module
 * prevents is calling that route more than once (a retry storm) or calling it
 * when there is nothing to claim. Everything the store needs in order to answer
 * "should I spend the claim?" is a plain value, so it is unit-tested instead of
 * clicked at:
 * `node --import tsx --test src/services/hubIdentityClaim.test.ts`.
 *
 * Nothing here talks to the network, reads storage, or knows about Vue/Pinia.
 */

/** The cloud session reached a hub that has no `cloud_user_id` mapping for it. */
export const IDENTITY_NOT_LINKED = 'identity_not_linked';
/** The cloud token is valid, but no enabled hub user carries that email. */
export const NO_MATCHING_USER = 'no_matching_user';
/** The matching hub user exists but is disabled. */
export const USER_DISABLED = 'user_disabled';
/** The hub could not verify the cloud token (expired / revoked / wrong cloud). */
export const INVALID_CLOUD_TOKEN = 'invalid_cloud_token';

/**
 * The subset of the error `apiFetch` throws that this decision reads.
 *
 * `src/services/api.ts` wraps a failed response in `createApiError()` — an
 * `Error` carrying `status`, `statusText` and the parsed body in `data`. The
 * axios-shaped `response.data` is accepted too: callers that go through axios
 * (`runtimeJsonRequest`) surface the same body one level deeper.
 */
export interface HubApiErrorLike {
  status?: number;
  /** Parsed error body, where `apiFetch` puts it. */
  data?: { code?: string | null; error?: string | null } | null;
  /** axios-shaped caller (`error.response.data.code`). */
  response?: { status?: number; data?: { code?: string | null } | null } | null;
}

/** The machine-readable `code` a hub refusal carries, if any. */
export function hubFailureCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const candidate = error as HubApiErrorLike;
  const code = candidate.data?.code ?? candidate.response?.data?.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** True when the hub refused the call because this cloud identity is unmapped. */
export function isIdentityNotLinked(error: unknown): boolean {
  return hubFailureCode(error) === IDENTITY_NOT_LINKED;
}

export interface ClaimGuardInput {
  /** The signed-in cloud account's access token (`authStore.cloudAccessToken`). */
  cloudAccessToken?: string | null;
  /** The hub's own token (`authStore.accessToken`) — set once a session is mapped. */
  localAccessToken?: string | null;
  /** The failure that just happened (the `/users/me` call the tunnel refused). */
  lastError?: unknown;
  /** Latched true by the caller on the first attempt — the anti-storm gate. */
  alreadyAttempted?: boolean;
}

/**
 * Should the client spend the ONE `POST /auth/claim` recovery call?
 *
 * Yes only when every one of these holds:
 *
 * - a cloud account is signed in — without a cloud token there is nothing to
 *   validate, and the route would answer `400`;
 * - the session has no local hub token yet — with one, the identity is already
 *   mapped and `linkCloudUserIfNeeded()` owns that (different) repair;
 * - the hub just answered `identity_not_linked` — the *only* refusal that means
 *   "claim me". A network failure, a channel that is still coming up, a disabled
 *   user or an invalid cloud token must not provoke a claim;
 * - no claim was attempted yet in this session. The caller latches
 *   `alreadyAttempted` before its first attempt, which is what makes this
 *   one-shot even though the connect path retries `fetchHubUser()`.
 *
 * `no_matching_user` deliberately returns false: retrying cannot help (the hub
 * never creates a user), so the caller surfaces "an administrator must invite
 * this email" instead of looping.
 */
export function shouldAttemptClaim(input: ClaimGuardInput): boolean {
  if (input.alreadyAttempted) return false;
  if (!input.cloudAccessToken) return false;
  if (input.localAccessToken) return false;
  return isIdentityNotLinked(input.lastError);
}

/**
 * Should the client link itself to a hub the person just PICKED?
 *
 * The second door into the same link (card `t_dc2d87c6`). `shouldAttemptClaim`
 * above answers the *tunnel's* refusal — a cloud session the hub will not map.
 * On the hub's own LAN there is no tunnel and therefore no `identity_not_linked`:
 * a window that holds nothing for that hub simply answers `401 Not authenticated`
 * to every call — and a hub on that network is joined by signing in, which is
 * what `components/hub/JoinHubSheet.vue` drives. (An invited person used to be
 * handed a pairing code here; the code chain is gone, card `t_2f0c4a91`.)
 *
 * The differences from the tunnel case, each deliberate:
 *
 * - **The trigger is an action, never an ambient retry.** This is only asked
 *   when a person chose a hub on this network, so a 401 on its own still never
 *   provokes a claim;
 * - **`localAccessToken` is not consulted.** The store holds ONE hub session for
 *   the *active* hub; a person signed into hub A who picks hub B on the same
 *   network has a local token and still needs the link for B (the caller keeps
 *   the current hub until the join succeeds);
 * - **`alreadyBound` is the gate that replaces it**: if this device already holds
 *   a credential or a saved connection for THAT hub, there is nothing to link —
 *   the card offers Connect, not Join (`resolveDiscoveredHubPlan`).
 *
 * A refusal is final for the session: `alreadyAttempted` is latched *before* the
 * call by the same rule as above, so a `no_matching_user` never becomes a loop.
 */
export interface LinkGuardInput {
  /** The signed-in cloud account's access token. */
  cloudAccessToken?: string | null;
  /** This device already holds something for that hub (credential or saved row). */
  alreadyBound?: boolean;
  /** Latched true by the caller before its first link attempt. */
  alreadyAttempted?: boolean;
}

export function shouldAttemptLink(input: LinkGuardInput): boolean {
  if (input.alreadyAttempted) return false;
  if (!input.cloudAccessToken) return false;
  if (input.alreadyBound) return false;
  return true;
}

export type ClaimFailureCode = typeof NO_MATCHING_USER | typeof USER_DISABLED | typeof INVALID_CLOUD_TOKEN | 'unknown';

/** Bucket a claim failure so the UI copy can name the actual cause. */
export function classifyClaimFailure(error: unknown): ClaimFailureCode {
  const code = hubFailureCode(error);
  if (code === NO_MATCHING_USER || code === USER_DISABLED || code === INVALID_CLOUD_TOKEN) {
    return code;
  }
  return 'unknown';
}

/**
 * Locale key for each refusal. The copy itself lives in
 * `src/locales/{en,sl}.ts` — never in the store (PLAN.md §4 house rules).
 *
 * `no_matching_user` is the one a person needs to act on: nobody can invite
 * themselves, an administrator has to.
 */
export function claimFailureMessageKey(code: ClaimFailureCode): string {
  switch (code) {
    case NO_MATCHING_USER:
      return 'auth.claim.noMatchingUser';
    case USER_DISABLED:
      return 'auth.claim.userDisabled';
    case INVALID_CLOUD_TOKEN:
      return 'auth.claim.invalidCloudToken';
    default:
      return 'auth.claim.failed';
  }
}

export interface ClaimedIdentity<TUser> {
  token: string;
  user: TUser | null;
}

/**
 * Read `{user, token}` out of a successful claim response.
 *
 * Returns null when the response carries no usable token: adopting half of a
 * backend answer would leave the app "connected" with no hub session — the
 * half-connected state this flow exists to avoid.
 */
export function readClaimedIdentity<TUser>(body: unknown): ClaimedIdentity<TUser> | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as { token?: unknown; user?: unknown };
  if (typeof raw.token !== 'string' || raw.token.length === 0) return null;
  const user = raw.user && typeof raw.user === 'object' ? (raw.user as TUser) : null;
  return { token: raw.token, user };
}
